/**
 * Date Formatting Utilities
 *
 * Centralized date and time formatting functions for consistent display.
 *
 * EVERY date and time here is read in the current organization's timezone,
 * never the viewer's device timezone. Event dates are stored as midnight in
 * the org's zone (EventForm), so reading them on a device even one hour west
 * of the org lands on the PREVIOUS calendar day — rmu crew on Central-time
 * phones saw a Saturday 12:00 PM ET crew call as "Friday 11:00 AM" (2026-10-05).
 * The backend (emails, digests, ICS feed) already formats in the org zone;
 * this keeps the app in step with it.
 *
 * The zone is module state, set by OrganizationProvider whenever the current
 * org changes (the API client keeps the current org slug the same way). Tests
 * default it to UTC in src/test/setup.ts; any test about zones must set one
 * explicitly with setDisplayTimeZone.
 */

/** Same default as `Organization.timezone` in schema.prisma and the backend. */
export const DEFAULT_TIME_ZONE = 'America/New_York';

let displayTimeZone = DEFAULT_TIME_ZONE;

function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Set the IANA timezone every helper in this file reads in. Unknown or empty
 * values fall back to DEFAULT_TIME_ZONE rather than throwing at render time.
 */
export function setDisplayTimeZone(timeZone: string | null | undefined): void {
  displayTimeZone = timeZone && isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
}

/** The IANA timezone dates and times are currently displayed in. */
export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

// Intl.DateTimeFormat construction is the expensive part; lists render
// hundreds of dates, so keep one formatter per (zone, options) pair.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${displayTimeZone}|${JSON.stringify(options)}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.DateTimeFormat('en-US', { ...options, timeZone: displayTimeZone });
    formatterCache.set(key, cached);
  }
  return cached;
}

/**
 * Format an instant in the display timezone. Use this instead of
 * `toLocaleDateString` / `toLocaleTimeString`, which read the device clock.
 */
export function formatInTimeZone(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions
): string {
  return formatter(options).format(new Date(value));
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function zonedParts(instant: Date): ZonedParts {
  const parts = formatter({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** Offset of the display timezone from UTC at an instant, in milliseconds. */
function zoneOffsetMs(instant: Date): number {
  const p = zonedParts(instant);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * The instant of a wall-clock time on a YYYY-MM-DD calendar day in the display
 * timezone. Two passes so a DST change between the guess and the answer is
 * still handled; a wall time that does not exist (spring-forward gap) lands
 * an hour later, like the device-local `new Date(y, m, d, h)` it replaces.
 */
export function zonedWallTimeToDate(
  dateInput: string,
  hours = 0,
  minutes = 0,
  seconds = 0
): Date {
  const [y, m, d] = dateInput.split('-').map(Number);
  const wallAsUtc = Date.UTC(y!, m! - 1, d!, hours, minutes, seconds);
  const firstOffset = zoneOffsetMs(new Date(wallAsUtc));
  let instant = wallAsUtc - firstOffset;
  const secondOffset = zoneOffsetMs(new Date(instant));
  if (secondOffset !== firstOffset) {
    instant = wallAsUtc - secondOffset;
  }
  return new Date(instant);
}

/** "YYYY-MM-DD" of an instant as observed in the display timezone. */
export function zonedDayKey(value: string | number | Date): string {
  const p = zonedParts(new Date(value));
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** "HH:MM" (24h) wall-clock time of an instant in the display timezone. */
export function zonedTimeInputValue(value: string | number | Date): string {
  const p = zonedParts(new Date(value));
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/**
 * Short zone label for the display timezone at an instant ("EDT", "CST";
 * zones without a US abbreviation come out as e.g. "GMT+1"). Pass the event's
 * own time so a November event viewed in October reads "EST", not "EDT".
 */
export function formatTimeZoneAbbr(value?: string | number | Date | null): string {
  const instant = value ? new Date(value) : new Date();
  const parts = formatter({ timeZoneName: 'short' }).formatToParts(instant);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

/**
 * Format a date string to a full date (e.g., "Jan 15, 2026")
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set';
  return formatInTimeZone(dateString, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string to a short date (e.g., "Jan 15")
 */
export function formatShortDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set';
  return formatInTimeZone(dateString, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string to a full long date (e.g., "Wednesday, January 15, 2026")
 */
export function formatLongDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set';
  return formatInTimeZone(dateString, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string to show weekday (e.g., "Sat, Jan 15")
 */
export function formatDateWithWeekday(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set';
  return formatInTimeZone(dateString, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string with weekday + year (e.g., "Sat, Jan 15, 2026").
 * Common combo for event-card headers.
 */
export function formatShortDateWithWeekday(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set';
  return formatInTimeZone(dateString, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a time string to display time (e.g., "5:30 PM")
 */
export function formatTime(timeString: string | null | undefined): string {
  if (!timeString) return 'Not set';
  return formatInTimeZone(timeString, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a time with the org's zone label (e.g., "5:30 PM EDT"). Use wherever
 * an event time is shown to crew, who may be viewing from another zone.
 */
export function formatTimeWithZone(timeString: string | null | undefined): string {
  if (!timeString) return 'Not set';
  return `${formatTime(timeString)} ${formatTimeZoneAbbr(timeString)}`;
}

/**
 * Format a time-only string (HH:MM) to display time (e.g., "5:30 PM")
 * Used when time is stored separately from date.
 */
export function formatTimeOnly(timeString: string | null | undefined): string {
  if (!timeString) return '';
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a date and optional time for event display (e.g., "Jan 15, 2026 at 5:30 PM")
 */
export function formatDateTime(
  dateString: string | null | undefined,
  timeString?: string | null
): string {
  if (!dateString) return 'Not set';
  const date = formatDate(dateString);
  if (!timeString) return date;
  const time = formatTime(timeString);
  return `${date} at ${time}`;
}

/**
 * Format a time range (e.g., "5:30 PM - 10:00 PM"). With `withZone`, the
 * zone label is appended once, after the range ("5:30 PM - 10:00 PM EDT").
 */
export function formatTimeRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  { withZone = false }: { withZone?: boolean } = {}
): string {
  const start = startTime ? formatTime(startTime) : null;
  const end = endTime ? formatTime(endTime) : null;
  const zone = withZone ? ` ${formatTimeZoneAbbr(startTime || endTime)}` : '';

  if (start && end) return `${start} - ${end}${zone}`;
  if (start) return `${start}${zone}`;
  return 'Not set';
}

/** Whole calendar days from `fromKey` to `toKey` (both YYYY-MM-DD). */
function dayKeyDiff(fromKey: string, toKey: string): number {
  const toUtc = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return Date.UTC(y!, m! - 1, d!);
  };
  return Math.round((toUtc(toKey) - toUtc(fromKey)) / (1000 * 60 * 60 * 24));
}

/**
 * Get relative time description (e.g., "Today", "Tomorrow", "In 3 days")
 */
export function getRelativeDay(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const diffDays = dayKeyDiff(zonedDayKey(new Date()), zonedDayKey(dateString));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;

  return formatDate(dateString);
}

/**
 * Returns the instant of midnight (00:00) today in the display timezone.
 * Use this for "upcoming" / "past" filters so that an event happening
 * today still counts as upcoming throughout the entire day.
 */
export function startOfToday(): Date {
  return zonedWallTimeToDate(zonedDayKey(new Date()));
}

/**
 * Check if a date is strictly before today (today does NOT count as past).
 */
export function isPastDate(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  return new Date(dateString) < startOfToday();
}

/**
 * Compare two items by event date, then by start time. Same-day events
 * sort by start time ascending; items without a start time sort last
 * within their day. Use with Array.prototype.sort().
 */
export function compareByDateThenStart<
  T extends { eventDate: string; startTime?: string | null }
>(a: T, b: T): number {
  const dateDiff = new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
  if (dateDiff !== 0) return dateDiff;
  const aStart = a.startTime ? new Date(a.startTime).getTime() : Number.POSITIVE_INFINITY;
  const bStart = b.startTime ? new Date(b.startTime).getTime() : Number.POSITIVE_INFINITY;
  return aStart - bStart;
}

/**
 * Combine a stored event date (ISO datetime at org-zone midnight) with a
 * wall-clock "HH:MM" string into a full ISO datetime on that same calendar
 * day in the display timezone. Mirrors EventForm's time handling, but starts
 * from the stored eventDate instead of a YYYY-MM-DD picker value — used by
 * bulk time updates, where each event keeps its own date and only the time
 * changes.
 */
export function combineEventDateWithTime(
  eventDateIso: string,
  time: string
): string {
  const [hours, minutes] = time.split(':').map(Number);
  return zonedWallTimeToDate(zonedDayKey(eventDateIso), hours, minutes).toISOString();
}

/**
 * Convert a YYYY-MM-DD picker value into the ISO instant of midnight on that
 * day in the display timezone. `new Date('2026-09-16')` is parsed as UTC
 * *midnight*, a different instant than localDayEndIso's end of day — mixing
 * the two makes a range ending Sep 15 look like it runs past the start of a
 * range beginning Sep 16. Same reasoning as EventForm's eventDate handling.
 */
export function localDayStartIso(dateInput: string): string {
  return zonedWallTimeToDate(dateInput).toISOString();
}

/**
 * Convert a YYYY-MM-DD picker value into the ISO instant of the last second of
 * that day in the display timezone. Use for the end of an inclusive date range.
 */
export function localDayEndIso(dateInput: string): string {
  return zonedWallTimeToDate(dateInput, 23, 59, 59).toISOString();
}

/**
 * Format a stored ISO datetime as the YYYY-MM-DD value an <input type="date">
 * expects, reading calendar fields in the display timezone.
 * `.toISOString().split('T')[0]` would report the next day for an end-of-day
 * timestamp west of UTC.
 */
export function toDateInputValue(dateString: string): string {
  return zonedDayKey(dateString);
}

/**
 * Check if a date is today
 */
export function isToday(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  return zonedDayKey(dateString) === zonedDayKey(new Date());
}
